/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
/**
 * ProjectReducer.ts
 * -----------------
 * Contains the main reducer function for Project State.
 *
 * - Implements the ProjectReducer(state, action) function which handles all state transitions via actions defined in ProjectActions.ts.
 * - Central location for all business logic regarding how actions update the project state.
 * - Imports action types, helper utilities, and persistence logic from related files.
 *
 * This file should be the only reducer for ProjectState and is the core of the state machine pattern used by project-related components.
 */
import {
  ComposerNodeDescriptor,
  FileType,
  InputNodeDescriptor,
  OutputNodeDescriptor,
} from "../Api/Api";
import {
  applyEdgeChanges,
  applyNodeChanges,
  EdgeRemoveChange,
  NodeRemoveChange,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  normalizeRawMappingResult,
  isFileInfoPair,
} from "../TransformEngine/TransformNodes/MappingResultUtils";
import { IfFulfilled, ReducePromise } from "../Informedb/PromiseState";
import { loggedNotify, Component } from "../Shared/Notify";
import {
  addBrandNewEntry,
  addSpecificEntry,
  getInputNodeAndPathsForComposer,
  modifyComposerNodeData,
  modifyNodeData,
  modifyOutputNodeData,
  PARENT_HANDLE_ID,
  ReactFlowEdge,
  ReactFlowNode,
  TransformEngineNodeData,
} from "../TransformEngine/TransformNodes/Util/TransformEngineDataUtils";
import {
  nodeFromDescriptor,
  TRANSFORM_NODE_TYPE_TO_ID,
} from "../TransformEngine/NodeDescriptorUtil";
import { ProjectState } from "./ProjectModels";
import {
  ProjectAction,
  ReconcileConnectionsAction,
  PropagateTransformToOutputsAction,
  MappingSavedAsNewOutcome,
  SaveMappingAsNewAction,
} from "./ProjectActions";
import {
  addNodeFromDescriptor,
  buildGranualsFromAllConnectedInputs,
  doConnection,
  isInputNodeDescriptor,
  loadProjectData,
  performMapping,
  propagateToOutputNodes,
  propagateTransformToOutputsInReducer,
  resetUnusedOutputAndComposerNodes,
  saveMapping,
  saveMappingAsNew,
  toMappingEdge,
} from "./ProjectUtils";
import { mergeProject, parse } from "./ProjectPersistence";
import { isTransformNodeType, NODE_TYPES } from "../Shared/TransformNodeTypes";
import { Granual } from "../Api/DataTransformation";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";

/**
 * Resolves the full-file transform UUID for a given input type and target
 * output format, reusing the original transform-node ids so there is one
 * source of truth for these UUIDs. Returns undefined when no full-file
 * transform exists for the pair.
 */
function getFullFileTransformId(
  sourceType: FileType,
  targetFormat: FileType
): string | undefined {
  if (sourceType === FileType.PROTO && targetFormat === FileType.XML) {
    return TRANSFORM_NODE_TYPE_TO_ID[NODE_TYPES.protoToXml_full];
  }
  if (sourceType === FileType.XML && targetFormat === FileType.PROTO) {
    return TRANSFORM_NODE_TYPE_TO_ID[NODE_TYPES.xmlToProto_full];
  }
  return undefined;
}

export function ProjectReducer(
  state: ProjectState,
  action: ProjectAction
): ProjectState {
  const { currentTheme } = useDexterTheme();
  switch (action[0]) {
    case "navigate": {
      const [
        {},
        { apolloClient, axiosContext, siteProps, isActiveRef },
        dispatch,
      ] = action;

      // for the time being, always kick off data loading when we navigate to the page
      const dataLoadAsync = ReducePromise(state.dataLoadAsync, [
        "start",
        (cancel) => loadProjectData(axiosContext, cancel),
        // the following dispatch call is what causes the "Can't perform a React state update on an unmounted component" warning to be shown on the console if the page is refreshed
        (outcome) => {
          // If we don't provide a ref, always fire the new action.  If we do provide a ref, make sure it's current (i.e. the caller hasn't cleaned up)
          if (isActiveRef === undefined || isActiveRef.current) {
            dispatch(["dataLoaded", outcome]);
          }
        },
      ]);

      return {
        ...state,
        apolloClient,
        axiosContext,
        siteProps,
        dataLoadAsync,
        edgeClicked: false,
      };
    }

    case "edgeClicked": {
      return {
        ...state,
        edgeClicked: true,
      };
    }

    case "startTitleEditing": {
      const [{}, { nodeId }] = action;
      const existingNodes = state.rfNodes;
      const existingNode = existingNodes.find(
        (node) => node.data.dataID == nodeId
      );
      if (!existingNode) {
        return state;
      }

      const oldNodeData = existingNode.data;

      const newNodeData: TransformEngineNodeData = {
        ...oldNodeData,
        titleEditInProgress: true,
        titleInProgress: oldNodeData.title ?? "",
      };

      return {
        ...state,
        rfNodes: modifyNodeData(existingNodes, newNodeData),
      };
    }

    case "updateComposerNodeTitle": {
      const [{}, { nodeId }] = action;
      const existingNodes = state.rfNodes;
      const existingNode = existingNodes.find(
        (node) => node.data.dataID == nodeId
      );
      if (!existingNode) {
        return state;
      }

      const descriptor = existingNode.data.descriptor as ComposerNodeDescriptor;

      const oldNodeData = existingNode.data;

      const newNodeData: TransformEngineNodeData = {
        ...oldNodeData,
        title: `${
          descriptor.sourceInputNode.displayName
        }_to_${descriptor.type.toLocaleLowerCase()}`,
      };

      return {
        ...state,
        rfNodes: modifyNodeData(existingNodes, newNodeData),
      };
    }

    case "setOutputFormat": {
      const [{}, { nodeId, targetFormat }] = action;
      const existingNodes = state.rfNodes;
      const existingNode = existingNodes.find(
        (node) => node.data.dataID == nodeId
      );
      if (!existingNode) {
        return state;
      }

      const descriptor = existingNode.data.descriptor as ComposerNodeDescriptor;

      const modifiedComposerNodeDescriptor: ComposerNodeDescriptor = {
        ...descriptor,
        targetFormat,
      };

      return {
        ...state,
        rfNodes: modifyComposerNodeData(
          existingNodes,
          existingNode.data,
          modifiedComposerNodeDescriptor
        ),
      };
    }

    case "updateTitleEdit": {
      const [{}, { nodeId, wipTitle }] = action;
      const existingNodes = state.rfNodes;
      const existingNode = existingNodes.find(
        (node) => node.data.dataID == nodeId
      );
      if (!existingNode) {
        return state;
      }

      const oldNodeData = existingNode.data;

      const newNodeData: TransformEngineNodeData = {
        ...oldNodeData,
        titleEditInProgress: true,
        titleInProgress: wipTitle,
      };

      return {
        ...state,
        rfNodes: modifyNodeData(existingNodes, newNodeData),
      };
    }

    case "confirmTitleEdit": {
      const [{}, { nodeId }] = action;
      const existingNodes = state.rfNodes;
      const existingNode = existingNodes.find(
        (node) => node.data.dataID == nodeId
      );
      if (!existingNode) {
        return state;
      }

      const oldNodeData = existingNode.data;

      const newNodeData: TransformEngineNodeData = {
        ...oldNodeData,
        titleEditInProgress: false,
        title: oldNodeData.titleInProgress,
      };

      return {
        ...state,
        rfNodes: modifyNodeData(existingNodes, newNodeData),
      };
    }

    case "discardTitleEdit": {
      const [{}, { nodeId }] = action;
      const existingNodes = state.rfNodes;
      const existingNode = existingNodes.find(
        (node) => node.data.dataID == nodeId
      );
      if (!existingNode) {
        return state;
      }

      const oldNodeData = existingNode.data;

      const newNodeData: TransformEngineNodeData = {
        ...oldNodeData,
        titleEditInProgress: false,
        title: oldNodeData.title,
      };

      return {
        ...state,
        rfNodes: modifyNodeData(existingNodes, newNodeData),
      };
    }

    case "startProjectTitleEditing": {
      return {
        ...state,
        projectTitleEditInProgress: true,
        projectTitleInProgress: state.projectName,
      };
    }

    case "updateProjectTitle": {
      const [{}, { wipTitle }] = action;

      return {
        ...state,
        projectTitleEditInProgress: true,
        projectTitleInProgress: wipTitle,
      };
    }

    case "confirmProjectTitleEdit": {
      const newProjectData: ProjectState = {
        ...state,
        projectTitleEditInProgress: false,
        projectName: state.projectTitleInProgress,
      };
      return newProjectData;
    }

    case "discardProjectTitleEdit": {
      return {
        ...state,
        projectTitleEditInProgress: false,
        projectTitleInProgress: state.projectName,
      };
    }

    case "dataLoaded": {
      const [{}, outcome] = action;
      const dataLoadAsync = ReducePromise(state.dataLoadAsync, outcome);
      const data = IfFulfilled(dataLoadAsync);

      return {
        ...state,
        dataLoadAsync,
        bufferedProjects: data?.projects ?? state.bufferedProjects,
        edgeClicked: false,
      };
    }

    case "loadProjectFromData": {
      const [{}, { project }] = action;
      // Rebuild nodes when we load the project so we can populate
      // the nodes with the contents from the file its associated with
      const rebuiltNodes = project.rfNodes.map((node) => {
        const descriptor = node.data.descriptor;
        if (descriptor) {
          const rebuiltNode = nodeFromDescriptor(
            node.id,
            descriptor,
            node.position
          );

          const dataID = node.data.dataID as string | undefined;
          const defaultTitleFromDataID = dataID?.replace(/\d+$/, ""); // strip trailing instance number

          const savedTitle = node.data.title as string | undefined;
          const isUserDefined =
            savedTitle !== undefined && savedTitle !== defaultTitleFromDataID;

          // This will strip mappingResult regardless of the save file.
          return {
            ...rebuiltNode,
            data: {
              ...rebuiltNode.data,
              title: isUserDefined ? savedTitle : rebuiltNode.data.title,
              descriptor: {
                ...rebuiltNode.data.descriptor,
                displayName: isUserDefined
                  ? savedTitle
                  : rebuiltNode.data.title,
              },
              mappingResult: undefined, // reset mappingResult
            },
          };
        }
        return node;
      });

      return {
        ...mergeProject(state, project),
        rfNodes: rebuiltNodes,
        bufferedProjects: state.bufferedProjects,
        inputNodeDescriptors: project.inputNodeDescriptors ?? [],
        edgeClicked: false,
      };
    }

    case "reconcileConnections": {
      const [{}, { silent }, dispatch] = action as ReconcileConnectionsAction;
      const outputNodesUpdated = new Set<string>();
      const composerNodesUpdated = new Set<string>();
      const mappingPromises: Promise<void>[] = [];
      let sourceNodeDescriptor: InputNodeDescriptor | null = null;

      state.rfNodes.forEach((transformNode: any) => {
        if (isTransformNodeType(transformNode.type)) {
          // Check for parent handle connection (full-file transform)
          const parentHandleEdge = state.rfEdges.find(
            (edge) =>
              edge.target === transformNode.data.dataID &&
              edge.sourceHandle === PARENT_HANDLE_ID
          );

          let granuals: Granual[] | null = null;
          let fileId: string | undefined = undefined;

          if (parentHandleEdge) {
            // Full-file transform: extract fileId from parent edge
            const sourceNode = state.rfNodes.find(
              (node) => node.data.dataID === parentHandleEdge.source
            );

            sourceNodeDescriptor = sourceNode.data
              .descriptor as InputNodeDescriptor;

            if (sourceNode) {
              fileId = (sourceNode.data.descriptor as InputNodeDescriptor)
                ?.associatedFileId;
            }
          } else {
            // Granular transform: collect from all connected inputs
            const granualsData = buildGranualsFromAllConnectedInputs(
              state.rfEdges,
              state.rfNodes,
              transformNode.data.dataID
            );

            // Skip if no inputs connected
            if (
              granualsData.granuals.length === 0 &&
              granualsData.fileIds.length === 0
            ) {
              return;
            }

            granuals =
              granualsData.granuals.length > 0 ? granualsData.granuals : null;
          }

          // Skip if we have neither granuals nor fileId
          if (!granuals && !fileId) {
            return;
          }

          const promise = performMapping(
            state,
            transformNode.data.descriptor.id,
            null,
            granuals,
            fileId, // Pass fileId for full-file transforms
            silent // Pass silent flag to suppress notifications during bulk load
          ).then((rawResult) => {
            const normalizedResult = normalizeRawMappingResult(rawResult);

            const modifiedTransformNodeData: TransformEngineNodeData = {
              ...transformNode.data,
              mappingResult: normalizedResult,
            };
            dispatch([
              "modifyNodeData",
              { modifiedNodeData: modifiedTransformNodeData },
            ]);

            propagateToOutputNodes(
              transformNode,
              normalizedResult,
              state.rfEdges,
              state.rfNodes,
              dispatch,
              outputNodesUpdated,
              composerNodesUpdated
            );
          });

          mappingPromises.push(promise);
        }
      });

      if (mappingPromises.length === 0) {
        return state;
      }

      Promise.all(mappingPromises).then(() => {
        resetUnusedOutputAndComposerNodes(
          state.rfNodes,
          outputNodesUpdated,
          composerNodesUpdated,
          sourceNodeDescriptor,
          dispatch
        );
      });

      return {
        ...state,
        edgeClicked: false,
      };
    }

    case "propagateTransformToOutputs": {
      const [{}, { transformNodeID, silent }] =
        action as PropagateTransformToOutputsAction;

      const updatedNodes = propagateTransformToOutputsInReducer(
        state.rfNodes,
        state.rfEdges,
        transformNodeID,
        state.axiosContext,
        silent // Pass silent flag
      );

      return {
        ...state,
        rfNodes: updatedNodes,
        edgeClicked: false,
      };
    }

    case "loadProjectFromJson": {
      const [{}, { json, source }] = action;

      try {
        return parse(state, json);
      } catch (error) {
        loggedNotify.negative(
          state.axiosContext,
          Component.TRANSFORM_ENGINE,
          `Unable to parse JSON from ${source}: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
        return state;
      }
    }

    case "changeNodes": {
      const [{}, { changes }, dispatch] = action;

      const removeChanges = changes.filter(
        (change) => change.type == "remove"
      ) as NodeRemoveChange[];

      const edgeChanges = removeChanges.flatMap(
        (change: NodeRemoveChange): EdgeRemoveChange[] => {
          const edges = state.rfEdges.filter(
            (edge) => edge.source == change.id || edge.target == change.id
          );
          return edges.map((edge) => {
            return { id: edge.id, type: "remove" };
          });
        }
      );

      dispatch(["changeEdges", { changes: edgeChanges }, dispatch]);

      // Need to remove the descriptors if their respective nodes are removed from the mapping
      // in which case we can dissociate the file
      const nodeIdsToRemove = removeChanges.map((change) => change.id);
      const nodesToRemove = state.rfNodes.filter(
        (node) =>
          nodeIdsToRemove.includes(node.id) && node.type === NODE_TYPES.input
      );

      const fileIdsToRemove = nodesToRemove
        .map(
          (node) =>
            (node.data.descriptor as InputNodeDescriptor)?.associatedFileId
        )
        .filter((id): id is string => id !== null && id !== undefined);

      const updatedInputDescriptors = state.inputNodeDescriptors.filter(
        (desc) => !fileIdsToRemove.includes(desc.associatedFileId)
      );

      return {
        ...state,
        rfNodes: applyNodeChanges(changes, state.rfNodes) as ReactFlowNode[],
        inputNodeDescriptors: updatedInputDescriptors,
        edgeClicked: false,
      };
    }

    case "changeEdges": {
      const [{}, { changes }, dispatch] = action;

      let updatedEdges = applyEdgeChanges(
        changes,
        state.rfEdges
      ) as ReactFlowEdge[];

      let updatedNodes = state.rfNodes;

      const removedEdgeChanges = changes.filter(
        (nodeRemoveChange) => nodeRemoveChange.type === "remove"
      );
      const addedEdgeChanges = changes.filter(
        (nodeRemoveChange) => nodeRemoveChange.type === "add"
      );
      const selectedEdgeChanges = changes.filter(
        (nodeSelectChange) => nodeSelectChange.type === "select"
      );

      // Handle the removed edges
      if (removedEdgeChanges.length > 0) {
        removedEdgeChanges.forEach((removedChanges) => {
          const removedEdge = state.rfEdges.find(
            (e) => e.id === removedChanges.id
          );
          if (!removedEdge) return;

          const sourceNode = state.rfNodes.find(
            (node) => node.data.dataID === removedEdge.source
          );
          const targetNode = updatedNodes.find(
            (node) => node.data.dataID === removedEdge.target
          );

          if (!targetNode) return;

          const isInputNode = sourceNode?.type === NODE_TYPES.input;
          const isTransformNode = isTransformNodeType(targetNode?.type);
          const isOutputNode = targetNode?.type === NODE_TYPES.output;
          const isComposerNode = targetNode?.type === NODE_TYPES.composer;

          // Clear the mapping result
          if (isInputNode && isTransformNode) {
            dispatch(["reconcileConnections", { silent: true }, dispatch]);
          }

          // Clear output/composer nodes when their input edge is removed
          if (isOutputNode || isComposerNode) {
            const hasRemainingEdges = updatedEdges.some(
              (edge) => edge.target === removedEdge.target
            );

            if (!hasRemainingEdges) {
              const modifiedNodeData: TransformEngineNodeData = {
                ...targetNode.data,
                mappingResult: null,
              };

              if (isOutputNode) {
                const currentDescriptor = targetNode.data
                  .descriptor as OutputNodeDescriptor;

                const modifiedOutputNodeDescriptor: OutputNodeDescriptor = {
                  ...currentDescriptor,
                  isTransformReady: false,
                  type: FileType.UNDETECTED,
                };

                updatedNodes = modifyOutputNodeData(
                  updatedNodes,
                  modifiedNodeData,
                  modifiedOutputNodeDescriptor
                );
              } else if (isComposerNode) {
                const currentDescriptor = targetNode.data
                  .descriptor as ComposerNodeDescriptor;

                const modifiedComposerNodeDescriptor: ComposerNodeDescriptor = {
                  ...currentDescriptor,
                  type: FileType.UNDETECTED,
                  sourceInputNode: null,
                  composerData: [],
                };

                updatedNodes = modifyComposerNodeData(
                  updatedNodes,
                  modifiedNodeData,
                  modifiedComposerNodeDescriptor
                );
              }
            }
          }

          // Clear transform nodes that have no remaining input edges
          if (isTransformNode) {
            const hasRemainingInputEdges = updatedEdges.some(
              (edge) => edge.target === removedEdge.target
            );

            if (!hasRemainingInputEdges) {
              const modifiedTransformNodeData: TransformEngineNodeData = {
                ...targetNode.data,
                mappingResult: null,
              };
              updatedNodes = modifyNodeData(
                updatedNodes,
                modifiedTransformNodeData
              );
            }
          }
        });

        // Clear output/composer nodes connected to transform nodes that have no inputs
        updatedNodes.forEach((node) => {
          if (
            node.type === NODE_TYPES.output ||
            node.type === NODE_TYPES.composer
          ) {
            // Find the edge connecting to this output/composer node
            const incomingEdge = updatedEdges.find(
              (edge) => edge.target === node.data.dataID
            );

            if (incomingEdge) {
              // Find the source transform node
              const sourceTransformNode = updatedNodes.find(
                (n) => n.data.dataID === incomingEdge.source
              );

              if (
                sourceTransformNode &&
                isTransformNodeType(sourceTransformNode.type)
              ) {
                // Check if the transform node has any input edges
                const transformHasInputs = updatedEdges.some(
                  (edge) => edge.target === sourceTransformNode.data.dataID
                );

                // If transform has no inputs, clear this output/composer node
                if (!transformHasInputs) {
                  const modifiedNodeData: TransformEngineNodeData = {
                    ...node.data,
                    mappingResult: null,
                  };

                  if (node.type === NODE_TYPES.output) {
                    const currentDescriptor = node.data
                      .descriptor as OutputNodeDescriptor;

                    const modifiedOutputNodeDescriptor: OutputNodeDescriptor = {
                      ...currentDescriptor,
                      isTransformReady: false,
                      type: FileType.UNDETECTED,
                    };

                    updatedNodes = modifyOutputNodeData(
                      updatedNodes,
                      modifiedNodeData,
                      modifiedOutputNodeDescriptor
                    );
                  } else if (node.type === NODE_TYPES.composer) {
                    const currentDescriptor = node.data
                      .descriptor as ComposerNodeDescriptor;

                    const modifiedComposerNodeDescriptor: ComposerNodeDescriptor =
                      {
                        ...currentDescriptor,
                        type: FileType.UNDETECTED,
                        sourceInputNode: null,
                        composerData: [],
                      };

                    updatedNodes = modifyComposerNodeData(
                      updatedNodes,
                      modifiedNodeData,
                      modifiedComposerNodeDescriptor
                    );
                  }
                }
              }
            }
          }
        });

        // After clearing, re-propagate ONLY for transform nodes that still have inputs
        state.rfNodes.forEach((node) => {
          if (isTransformNodeType(node.type)) {
            const hasInputEdges = updatedEdges.some(
              (edge) => edge.target === node.data.dataID
            );

            // Only propagate if the transform node still has input edges
            if (hasInputEdges) {
              updatedNodes = propagateTransformToOutputsInReducer(
                updatedNodes,
                updatedEdges,
                node.data.dataID,
                state.axiosContext,
                true
              );
            }
          }
        });
      }

      // Handle added edges
      if (addedEdgeChanges.length > 0) {
        addedEdgeChanges.forEach((addedChanges) => {
          const addedEdge = updatedEdges.find(
            (edge) => edge.id === (addedChanges as any).id
          );
          if (!addedEdge) return;

          const sourceNode = updatedNodes.find(
            (node) => node.data.dataID === addedEdge.source
          );

          const isTransformNode = isTransformNodeType(sourceNode?.type);

          if (isTransformNode && sourceNode) {
            updatedNodes = propagateTransformToOutputsInReducer(
              updatedNodes,
              updatedEdges,
              sourceNode.data.dataID,
              state.axiosContext,
              true
            );
          }
        });
      }

      if (selectedEdgeChanges.length > 0) {
        selectedEdgeChanges.forEach((selectedChanges) => {
          const selectedEdge = updatedEdges.find(
            (edge) => edge.id === (selectedChanges as any).id
          );
          if (!selectedEdge) return;

          updatedEdges = updatedEdges.map((edge) =>
            edge.id === selectedEdge.id
              ? {
                  ...edge,
                  style: {
                    ...edge.style,
                    stroke: selectedEdge.selected
                      ? currentTheme.colors.edgeSelectedColor
                      : currentTheme.colors.edgeColor,
                    strokeWidth: selectedEdge.selected
                      ? currentTheme.edgeStrokeWidth.selected
                      : currentTheme.edgeStrokeWidth.default,
                  },
                }
              : edge
          );
        });
      }

      return {
        ...state,
        rfEdges: updatedEdges,
        rfNodes: updatedNodes,
      };
    }

    // Reset the theme of all edges on the transform engine
    // Then set the color and width of the edge based on the selected state
    // Prevents theme mismatch if theme is changed while edges contain a selected state
    case "reThemeEdges": {
      const [{}, { currentTheme }] = action;

      const updatedEdges = state.rfEdges.map((edge) => ({
        ...edge,
        style: {
          ...edge.style,
          stroke: edge.selected
            ? currentTheme.colors.edgeSelectedColor
            : currentTheme.colors.edgeColor,
          strokeWidth: edge.selected
            ? currentTheme.edgeStrokeWidth.selected
            : currentTheme.edgeStrokeWidth.default,
        },
      }));

      return {
        ...state,
        rfEdges: updatedEdges,
      };
    }

    case "removeEdge": {
      const [{}, { edgeId }] = action;

      const updatedEdges = state.rfEdges.filter((edge) => edge.id !== edgeId);

      // Also clear mapping result from target node if this was an input->transform edge
      let updatedNodes = state.rfNodes;
      const removedEdge = state.rfEdges.find((e) => e.id === edgeId);

      if (removedEdge) {
        const targetNode = state.rfNodes.find(
          (node) => node.data.dataID === removedEdge.target
        );

        if (targetNode && isTransformNodeType(targetNode.type)) {
          const modifiedTransformNodeData: TransformEngineNodeData = {
            ...targetNode.data,
            mappingResult: null,
          };
          updatedNodes = modifyNodeData(
            updatedNodes,
            modifiedTransformNodeData
          );
        }
      }

      return {
        ...state,
        rfEdges: updatedEdges,
        rfNodes: updatedNodes,
        edgeClicked: false,
      };
    }

    // Responsibilities of "connect":
    // - Connects new edge and validates it
    // - Normalizes and performs async mapping if target is transform node
    // - Passes mapped data if the target is an output node
    // - Updates download readiness if output is in completed state (chain isn't broken)
    //
    // Overall, should ensure readiness state of output nodes when some connection in a transform
    // chain is connected or disconnected.  Signal propogates to this case.
    case "connect": {
      const [{}, { params, isActiveRef }, dispatch] = action;

      // Add new edge if valid
      const updatedEdges = doConnection(state.rfEdges, params as ReactFlowEdge);
      if (updatedEdges.length === state.rfEdges.length) {
        return state;
      }

      const edge = toMappingEdge(state.rfNodes, params as ReactFlowEdge);
      if (!edge) {
        return { ...state, rfEdges: updatedEdges, edgeClicked: false };
      }

      const targetNode = state.rfNodes.find(
        (node) => edge.targetID === node.data.dataID
      );
      const sourceNode = state.rfNodes.find(
        (node) => edge.sourceID === node.data.dataID
      );

      let performMappingAsync = state.performMappingAsync;

      // Composer target: run the transform directly from the connected input,
      // in place of the old transform node. A full-file connection (the input's
      // parent handle) runs the full-file transform chosen by the composer's
      // target format. Granular connections are a no-op until that work lands.
      if (targetNode?.type === NODE_TYPES.composer && sourceNode) {
        const isFullFileConnection =
          (params as ReactFlowEdge).sourceHandle === PARENT_HANDLE_ID;

        if (isFullFileConnection) {
          const composerDescriptor = targetNode.data
            .descriptor as ComposerNodeDescriptor;
          const inputDescriptor = sourceNode.data
            .descriptor as InputNodeDescriptor;
          const targetFormat =
            composerDescriptor.targetFormat ?? FileType.PROTO;

          const transformId = getFullFileTransformId(
            inputDescriptor.type,
            targetFormat
          );

          if (!transformId) {
            loggedNotify.negative(
              state.axiosContext,
              Component.TRANSFORM_ENGINE,
              `No full-file transform is available from ${inputDescriptor.type} to ${targetFormat}.`
            );
          } else {
            const fileId = inputDescriptor.associatedFileId;

            performMappingAsync = ReducePromise(state.performMappingAsync, [
              "start",
              (cancel) =>
                performMapping(state, transformId, cancel, null, fileId),
              (outcome) => {
                if (outcome[2] === false) {
                  // Transform failed - remove the edge that was just added
                  const newEdge = updatedEdges.find(
                    (e) =>
                      e.source === edge.sourceID &&
                      e.target === edge.targetID &&
                      e.sourceHandle === (params as any).sourceHandle &&
                      e.targetHandle === (params as any).targetHandle
                  );

                  if (newEdge) {
                    dispatch(["removeEdge", { edgeId: newEdge.id }]);
                  }
                } else if (isActiveRef === undefined || isActiveRef.current) {
                  // Transform succeeded - write the result onto the composer
                  const normalizedResult = normalizeRawMappingResult(
                    outcome[2]
                  );
                  const isTransformReady = isFileInfoPair(normalizedResult);

                  const { pathGroups, sourceInputNode } =
                    getInputNodeAndPathsForComposer(
                      updatedEdges,
                      state.rfNodes,
                      targetNode
                    );

                  dispatch([
                    "modifyComposerNodeData",
                    {
                      modifiedNodeData: {
                        ...targetNode.data,
                        mappingResult: normalizedResult,
                      },
                      modifiedComposerNodeDescriptor: {
                        ...composerDescriptor,
                        type: isTransformReady
                          ? normalizedResult.fileType
                          : FileType.UNDETECTED,
                        sourceInputNode,
                        composerData: pathGroups,
                        isTransformReady,
                      },
                    },
                  ]);

                  if (isTransformReady) {
                    loggedNotify.positive(
                      state.axiosContext,
                      Component.TRANSFORM_ENGINE,
                      "Transform complete — output ready in the composer."
                    );
                  }
                }
              },
            ]);
          }
        }
        // Granular connection: no-op until granular transforms are wired up.
      } else if (targetNode?.type === NODE_TYPES.output) {
        // Legacy output-node handling. Output nodes are being removed
        // seperately, and the connection rules no longer allow
        // input -> output, so this branch is effectively dead.
        // TODO: Remove this legacy code for the output node removal PBI.
        const normalizedSource = normalizeRawMappingResult(
          sourceNode?.data.mappingResult
        );
        const isTransformReady = isFileInfoPair(normalizedSource);

        dispatch([
          "modifyOutputNodeData",
          {
            modifiedNodeData: {
              ...targetNode.data,
              mappingResult: normalizedSource,
            },
            modifiedOutputNodeDescriptor: {
              ...(targetNode.data.descriptor as OutputNodeDescriptor),
              isTransformReady,
              type: isTransformReady
                ? normalizedSource.fileType
                : FileType.UNDETECTED,
            },
          },
        ]);

        if (isTransformReady) {
          loggedNotify.positive(
            state.axiosContext,
            Component.TRANSFORM_ENGINE,
            "Output node connection successful. Output file ready for download"
          );
        }
      }

      return {
        ...state,
        rfEdges: updatedEdges,
        performMappingAsync,
        edgeClicked: false,
      };
    }

    case "createNodeFromDescriptor": {
      const [{}, { descriptor, position }] = action;

      return {
        ...state,
        rfNodes: addNodeFromDescriptor(
          descriptor,
          state.rfNodes,
          position // Pass position if present
        ) as ReactFlowNode[],
        edgeClicked: false,
        inputNodeDescriptors: [
          ...(state.inputNodeDescriptors || []),
          ...(isInputNodeDescriptor(descriptor)
            ? [descriptor as InputNodeDescriptor]
            : []),
        ],
      };
    }

    case "modifyNodeData": {
      const [{}, { modifiedNodeData }] = action;

      return {
        ...state,
        rfNodes: modifyNodeData(state.rfNodes, modifiedNodeData),
        edgeClicked: false,
      };
    }

    case "modifyOutputNodeData": {
      const [{}, { modifiedNodeData, modifiedOutputNodeDescriptor }] = action;

      return {
        ...state,
        rfNodes: modifyOutputNodeData(
          state.rfNodes,
          modifiedNodeData,
          modifiedOutputNodeDescriptor
        ),
        edgeClicked: false,
      };
    }

    case "modifyComposerNodeData": {
      const [{}, { modifiedNodeData, modifiedComposerNodeDescriptor }] = action;
      return {
        ...state,
        rfNodes: modifyComposerNodeData(
          state.rfNodes,
          modifiedNodeData,
          modifiedComposerNodeDescriptor
        ),
        edgeClicked: false,
      };
    }

    // Any component that invokes this action must also invoke updateNodeInternals since this changes the number of handles; see
    // https://reactflow.dev/docs/api/hooks/use-update-node-internals/
    case "addSpecificEntry": {
      const [{}, { nodeId, entryId, delta }] = action;
      const existingNode = state.rfNodes.find(
        (node) => node.data.dataID == nodeId
      );
      if (!existingNode) {
        return state;
      }

      const [newNodeData, {}] = addSpecificEntry(
        existingNode.data,
        entryId,
        delta
      );

      return {
        ...state,
        rfNodes: modifyNodeData(state.rfNodes, newNodeData),
        edgeClicked: false,
      };
    }

    case "addBrandNewEntry": {
      const [{}, { nodeId, path }] = action;
      const existingNode = state.rfNodes.find(
        (node) => node.data.dataID == nodeId
      );

      if (!existingNode) {
        return state;
      }
      const [newNodeData, {}] = addBrandNewEntry(existingNode.data, path);

      return {
        ...state,
        rfNodes: modifyNodeData(state.rfNodes, newNodeData),
        edgeClicked: false,
      };
    }

    case "saveMapping": {
      const [{}, { isActiveRef }, dispatch] = action;

      // kick off the processing
      const saveMappingAsync = ReducePromise(state.saveMappingAsync, [
        "start",
        (cancel) => saveMapping(state, cancel),
        (outcome) => {
          // If we don't provide a ref, always fire the new action.  If we do provide a ref, make sure it's current (i.e. the caller hasn't cleaned up)
          if (isActiveRef === undefined || isActiveRef.current) {
            dispatch(["mappingSaved", outcome]);
          }
        },
      ]);

      return {
        ...state,
        saveMappingAsync,
        edgeClicked: false,
      };
    }

    case "mappingSaved": {
      const [{}, outcome] = action;
      const saveMappingAsync = ReducePromise(state.saveMappingAsync, outcome);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const newProjects = IfFulfilled(saveMappingAsync) ?? [];

      return {
        ...state,
        saveMappingAsync,
        bufferedProjects:
          newProjects.length > 0 ? newProjects : state.bufferedProjects,
        edgeClicked: false,
      };
    }

    case "saveMappingAsNew": {
      const [{}, { newName, isActiveRef }, dispatch] =
        action as SaveMappingAsNewAction;

      const saveMappingAsync = ReducePromise(state.saveMappingAsync, [
        "start",
        (cancel) => saveMappingAsNew(state, newName, cancel),
        (outcome) => {
          if (isActiveRef === undefined || isActiveRef.current) {
            dispatch(["mappingSavedAsNew", outcome]);
          }
        },
      ]);

      return {
        ...state,
        saveMappingAsync,
        edgeClicked: false,
      };
    }

    case "mappingSavedAsNew": {
      const [{}, outcome] = action as MappingSavedAsNewOutcome;
      const saveMappingAsync = ReducePromise(state.saveMappingAsync, outcome);
      const newProjects = IfFulfilled(saveMappingAsync) ?? [];

      let updatedState = {
        ...state,
        saveMappingAsync,
        bufferedProjects:
          newProjects.length > 0 ? newProjects : state.bufferedProjects,
        edgeClicked: false,
      };

      if (newProjects.length > 0) {
        // Get the newly created project ID from session storage
        const newProjectID = sessionStorage.getItem("lastCreatedProjectID");
        sessionStorage.removeItem("lastCreatedProjectID");

        if (newProjectID) {
          // Find the newly created project
          const newProject = newProjects.find(
            (project) => project.projectID === newProjectID
          );

          if (newProject) {
            // Update current project to the newly created one
            updatedState = {
              ...updatedState,
              projectID: newProject.projectID,
              projectName: newProject.projectName,
            };
          }
        }
      }

      return updatedState;
    }

    case "mappingPerformed": {
      const [{}, outcome] = action;
      const performMappingAsync = ReducePromise(
        state.performMappingAsync,
        outcome
      );
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const success = IfFulfilled(performMappingAsync);

      return {
        ...state,
        performMappingAsync,
        edgeClicked: false,
      };
    }

    case "setBufferedProjects": {
      const [{}, { projects }] = action;
      return {
        ...state,
        bufferedProjects: projects,
      };
    }

    default:
      return state;
  }
}
